import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { buildSeed, TODAY, CHECKLISTS, setProfile } from "@/data/mock";
import { nowHM } from "@/lib/attendance";
import { api, apiError } from "@/lib/api";

const Ctx = createContext(null);

const SYNC = ["companies", "collaborators", "sales", "tasks", "checklists", "processes", "goals", "timeEntries", "absences", "maintenance", "accesses", "feedbacks", "events", "referrals"];
const byId = (arr = []) => new Map(arr.map((x) => [x.id, x]));

const diff = (prev, next) => {
  const ops = [];
  SYNC.forEach((coll) => {
    const pm = byId(prev[coll]);
    const nm = byId(next[coll]);
    nm.forEach((r, id) => pm.get(id) !== r && ops.push(["put", coll, r]));
    pm.forEach((_, id) => !nm.has(id) && ops.push(["del", coll, id]));
  });
  Object.entries(next.seals).forEach(([k, v]) => prev.seals[k] !== v && ops.push(["put", "seals", { ...v, id: k }]));
  Object.keys(prev.seals).forEach((k) => !next.seals[k] && ops.push(["del", "seals", k]));
  return ops;
};

const normalize = (s) => ({ trainings: [], progress: {}, templates: [], seals: {}, referrals: [], events: [], ...s, v: 8 });

const uid = (p) => `${p}-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

export function AppStoreProvider({ children }) {
  const [user, setUser] = useState(null);
  const [state, setState] = useState(null);
  const synced = useRef(null);
  const userRef = useRef(null);

  const loadState = useCallback(async (u) => {
    let { data } = await api.get("/state");
    if (u.role === "master" && !data.companies.length) {
      await api.post("/import-demo", buildSeed());
      ({ data } = await api.get("/state"));
    }
    const s = normalize(data);
    synced.current = s;
    setState(s);
  }, []);

  const start = useCallback(async (u) => {
    setProfile(u);
    userRef.current = u;
    await loadState(u);
    setUser(u);
  }, [loadState]);

  useEffect(() => {
    api.get("/auth/me").then((r) => start(r.data)).catch(() => setUser(false));
  }, [start]);

  useEffect(() => {
    if (!state || !synced.current || state === synced.current) return;
    const ops = diff(synced.current, state);
    synced.current = state;
    (async () => {
      try {
        for (const [op, coll, x] of ops) {
          if (op === "put") await api.put(`/records/${coll}`, x);
          else await api.delete(`/records/${coll}/${x}`);
        }
      } catch (e) {
        toast.error(apiError(e));
        loadState(userRef.current);
      }
    })();
  }, [state, loadState]);

  const session = useMemo(() => ({
    login: async (username, password) => {
      const { data } = await api.post("/auth/login", { username, password });
      await start(data);
      return data;
    },
    logout: async () => {
      await api.post("/auth/logout").catch(() => null);
      synced.current = null;
      userRef.current = null;
      setState(null);
      setUser(false);
    },
  }), [start]);

  const actions = useMemo(() => {
    const setItem = (clId, idx, fn) =>
      setState((s) => ({ ...s, checklists: s.checklists.map((c) => (c.id === clId ? { ...c, items: c.items.map((it, i) => (i === idx ? fn(it) : it)) } : c)) }));
    const upsert = (key, prefix) => (item) =>
      setState((s) => ({ ...s, [key]: item.id ? s[key].map((x) => (x.id === item.id ? { ...x, ...item } : x)) : [...s[key], { ...item, id: uid(prefix) }] }));
    const removeFrom = (key) => (id) => setState((s) => ({ ...s, [key]: s[key].filter((x) => x.id !== id) }));
    const log = (t, by, text) => ({ ...t, history: [...(t.history || []), { date: TODAY, by, text }] });
    const withStatus = (t, status) => ({ ...t, status, done: status === "concluida" });
    const mapTask = (id, fn) => setState((s) => ({ ...s, tasks: s.tasks.map((t) => (t.id === id ? fn(t) : t)) }));
    const STATUS_LOG = { pendente: "Marcada como pendente", andamento: "Marcada como em andamento", concluida: "Concluída", aguardando: "Concluída · aguardando aprovação" };
    return {
      saveTask: (task, by) =>
        setState((s) => ({
          ...s,
          tasks: task.id
            ? s.tasks.map((t) => (t.id === task.id ? log({ ...t, ...task }, by, "Tarefa editada") : t))
            : [...s.tasks, log({ ...task, id: uid("t"), status: "pendente", done: false, history: [] }, by, "Tarefa criada")],
        })),
      deleteTask: removeFrom("tasks"),
      setTaskStatus: (id, status, by) =>
        mapTask(id, (t) => {
          const next = status === "concluida" && t.requiresApproval ? "aguardando" : status;
          return log(withStatus(t, next), by, STATUS_LOG[next]);
        }),
      reviewTask: (id, approved, by, note) => mapTask(id, (t) => log(withStatus(t, approved ? "concluida" : "andamento"), by, approved ? "Aprovada" : `Devolvida para correção: ${note}`)),
      reopenTask: (id, by, note) =>
        mapTask(id, (t) => log(withStatus(t, "andamento"), by, `Reaberta para correção: ${note}`)),
      completeWithProof: (id, proof, by) =>
        mapTask(id, (t) => {
          const next = t.requiresApproval ? "aguardando" : "concluida";
          return log(withStatus({ ...t, proof: { ...proof, date: TODAY } }, next), by, `${STATUS_LOG[next]} (com foto)`);
        }),
      saveProcess: upsert("processes", "p"),
      deleteProcess: (id) => setState((s) => ({ ...s, processes: s.processes.filter((p) => p.id !== id), tasks: s.tasks.map((t) => (t.processId === id ? { ...t, processId: null } : t)) })),
      moveProcess: (id, dir) =>
        setState((s) => {
          const list = [...s.processes];
          const i = list.findIndex((p) => p.id === id);
          let j = i + dir;
          while (j >= 0 && j < list.length && list[j].companyId !== list[i].companyId) j += dir;
          if (j < 0 || j >= list.length) return s;
          [list[i], list[j]] = [list[j], list[i]];
          return { ...s, processes: list };
        }),
      saveChecklist: upsert("checklists", "cl"),
      punch: (c, type) =>
        setState((s) => {
          const ev = { type, time: nowHM() };
          const mine = (e) => e.collaboratorId === c.id && e.date === TODAY;
          const timeEntries = s.timeEntries.some(mine)
            ? s.timeEntries.map((e) => (mine(e) ? { ...e, events: [...e.events, ev] } : e))
            : [...s.timeEntries, { id: uid("te"), companyId: c.companyId, collaboratorId: c.id, date: TODAY, events: [ev] }];
          return { ...s, timeEntries };
        }),
      saveAbsence: upsert("absences", "ab"),
      deleteAbsence: removeFrom("absences"),
      deleteChecklist: removeFrom("checklists"),
      saveGoal: upsert("goals", "g"),
      deleteGoal: removeFrom("goals"),
      toggleTask: (id) => setState((s) => ({ ...s, tasks: s.tasks.map((t) => (t.id === id ? { ...t, done: !t.done } : t)) })),
      addTask: (task) => setState((s) => ({ ...s, tasks: [...s.tasks, { ...task, id: uid("t"), done: false }] })),
      toggleChecklistItem: (clId, idx) => setItem(clId, idx, (it) => ({ ...it, done: !it.done })),
      setProof: (clId, idx, proof) => setItem(clId, idx, (it) => ({ ...it, done: true, proof: { ...proof, status: "pendente", date: TODAY } })),
      reviewProof: (clId, idx, status) => setItem(clId, idx, (it) => ({ ...it, done: status === "aprovada", proof: { ...it.proof, status } })),
      addSale: (sale) => setState((s) => ({ ...s, sales: [{ ...sale, id: uid("s") }, ...s.sales] })),
      addFeedback: (fb) => setState((s) => ({ ...s, feedbacks: [{ ...fb, id: uid("f") }, ...s.feedbacks] })),
      advanceTraining: (cid, tid) =>
        setState((s) => {
          const cur = s.progress[cid]?.[tid] ?? 0;
          return { ...s, progress: { ...s.progress, [cid]: { ...s.progress[cid], [tid]: Math.min(100, cur + 25) } } };
        }),
      saveMaintenance: upsert("maintenance", "mt"),
      deleteMaintenance: removeFrom("maintenance"),
      checkMaintenance: (id, check) =>
        setState((s) => ({ ...s, maintenance: s.maintenance.map((m) => (m.id === id ? { ...m, lastCheck: { ...check, date: TODAY }, history: [...(m.history || []), { ...check, date: TODAY }] } : m)) })),
      saveAccess: upsert("accesses", "ac"),
      deleteAccess: removeFrom("accesses"),
      saveEvent: upsert("events", "ev"),
      deleteEvent: removeFrom("events"),
      setSeal: (companyId, seal) =>
        setState((s) => {
          const { [companyId]: _, ...rest } = s.seals;
          return { ...s, seals: seal ? { ...rest, [companyId]: { ...seal, approvedAt: TODAY } } : rest };
        }),
      addReferral: (r) => setState((s) => ({ ...s, referrals: [{ ...r, id: uid("rf"), date: TODAY }, ...s.referrals] })),
      reloadState: () => loadState(userRef.current),
      addCompany: (data) => setState((s) => ({ ...s, companies: [...s.companies, { ...data, id: uid("e"), history: [0, 0, 0, 0, 0] }] })),
      updateCompany: (id, data) => setState((s) => ({ ...s, companies: s.companies.map((c) => (c.id === id ? { ...c, ...data } : c)) })),
      addCollaborator: (data) =>
        setState((s) => {
          const id = uid("c");
          const checklists = CHECKLISTS.map((t, j) => ({ id: `cl-${id}-${j}`, companyId: data.companyId, collaboratorId: id, title: t.title, period: t.period, items: t.items.map((text) => ({ text, done: false })) }));
          const progress = Object.fromEntries(s.trainings.map((t) => [t.id, 0]));
          return { ...s, collaborators: [...s.collaborators, { ...data, id }], checklists: [...s.checklists, ...checklists], progress: { ...s.progress, [id]: progress } };
        }),
      updateCollaborator: (id, data) => setState((s) => ({ ...s, collaborators: s.collaborators.map((c) => (c.id === id ? { ...c, ...data } : c)) })),
      removeCollaborator: (id) =>
        setState((s) => {
          const { [id]: _, ...progress } = s.progress;
          const keep = (x) => x.collaboratorId !== id;
          return {
            ...s,
            progress,
            collaborators: s.collaborators.filter((c) => c.id !== id),
            tasks: s.tasks.filter(keep),
            checklists: s.checklists.filter(keep),
            feedbacks: s.feedbacks.filter(keep),
            timeEntries: s.timeEntries.filter(keep),
            absences: s.absences.filter(keep),
            maintenance: s.maintenance.map((m) => (m.assigneeId === id ? { ...m, assigneeId: null } : m)),
            companies: s.companies.map((c) => (c.maintenanceResponsibleId === id ? { ...c, maintenanceResponsibleId: null } : c)),
            sales: s.sales.map((e) => (e.collaboratorId === id ? { ...e, collaboratorId: null } : e)),
          };
        }),
    };
  }, [loadState]);

  return <Ctx.Provider value={{ state, user, ...session, ...actions }}>{children}</Ctx.Provider>;
}

export const useStore = () => useContext(Ctx);
